import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Alert, Button, Form, InputNumber, Skeleton, Switch } from 'antd'
import { createSchemaFieldRule } from 'antd-zod'
import type { z } from 'zod'
import { collectionAccountsSchema, collectionPolicySchema } from '@super-mega/server/contracts/customer-collection'
import { FormAnchorNav } from '@/components/form/form-anchor-nav.tsx'
import { FormPageContainer } from '@/components/form/form-page-container.tsx'
import { useTRPC } from '@/trpc/react.ts'

type Values = Pick<z.infer<typeof collectionPolicySchema>, 'enabled' | 'maxAmount' | 'accounts'>
const sections = [
  { id: 'collection-status', title: '自助出单' },
  { id: 'collection-limit', title: '金额与账期' },
  { id: 'collection-accounts', title: '收款账户' }
]
const rule = createSchemaFieldRule(collectionPolicySchema)
export function CustomerCollectionPolicy({ customerId }: { customerId: number }) {
  const trpc = useTRPC()
  const query = useQuery({
    ...trpc.customerCollection.policy.queryOptions({ customerId }),
    staleTime: Infinity,
    refetchOnWindowFocus: false
  })
  return (
    <FormPageContainer title="客户自助出单设置" breadcrumbRender={false}>
      {query.isPending && <Skeleton active />}
      {query.error && <Alert type="error" title={query.error.message} />}
      {query.isSuccess && (
        <PolicyForm
          key={`${customerId}:${query.data?.version ?? 0}`}
          customerId={customerId}
          initial={{
            enabled: query.data?.enabled ?? false,
            maxAmount: query.data?.maxAmount ?? '0.00',
            accounts: query.data ? collectionAccountsSchema.parse(query.data.accounts) : []
          }}
          version={query.data?.version ?? 0}
        />
      )}
    </FormPageContainer>
  )
}
function PolicyForm({ customerId, initial, version }: { customerId: number; initial: Values; version: number }) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const save = useMutation(
    trpc.customerCollection.savePolicy.mutationOptions({
      onSuccess: () => cache.invalidateQueries(trpc.customerCollection.policy.queryFilter({ customerId }))
    })
  )
  return (
    <Form<Values>
      initialValues={initial}
      layout="vertical"
      scrollToFirstError
      onFinish={(values) =>
        save.mutate(collectionPolicySchema.parse({ ...values, customerId, expectedVersion: version }))
      }
    >
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1 space-y-4">
          {save.error && <Alert type="error" title={save.error.message} />}
          <section id="section-collection-status" className="bg-surface scroll-mt-24 rounded-lg p-5">
            <h2 className="text-base font-medium">自助出单</h2>
            <Form.Item label="允许已绑定客户群生成并接收收款单" name="enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
            <p className="text-content-muted">
              开启后，群成员可要求机器人按已确认费用生成并出具收款单。群绑定仍须工作人员完成。
            </p>
          </section>
          <section id="section-collection-limit" className="bg-surface scroll-mt-24 rounded-lg p-5">
            <h2 className="text-base font-medium">金额与账期</h2>
            <Form.Item label="单次出单上限（元）" name="maxAmount" rules={[rule]} required>
              <InputNumber stringMode min="0" max="999999999999.99" precision={2} />
            </Form.Item>
            <p className="text-content-muted">
              首期只允许当前月份，按费用期间开始月份归集。待核费、非完整租赁账期或超过 100 张账单须人工处理。
            </p>
          </section>
          <section id="section-collection-accounts" className="bg-surface scroll-mt-24 rounded-lg p-5">
            <h2 className="text-base font-medium">收款账户</h2>
            <p className="text-content-muted">
              按经营主体指定唯一有效收款账户。主体和账户 ID
              可在主体设置中查询。当前发送文字收款通知，账户必须有户名和收款账号；二维码专用账户请人工处理。
            </p>
            <Form.List
              name="accounts"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!Array.isArray(value) || !value.length) throw new Error('至少配置一个收款账户')
                  }
                }
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  <div className="space-y-2">
                    {fields.map((field) => (
                      <div key={field.key} className="flex flex-wrap items-start gap-3">
                        <Form.Item
                          name={[field.name, 'organizationId']}
                          label="经营主体 ID"
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={1} precision={0} />
                        </Form.Item>
                        <Form.Item name={[field.name, 'accountId']} label="收款账户 ID" rules={[{ required: true }]}>
                          <InputNumber min={1} precision={0} />
                        </Form.Item>
                        <Button className="mt-7" danger onClick={() => remove(field.name)}>
                          删除
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Form.ErrorList errors={errors} />
                  <Button onClick={() => add()}>添加主体账户</Button>
                </>
              )}
            </Form.List>
          </section>
          <div className="bg-surface border-border sticky bottom-3 flex justify-end gap-3 rounded-lg border p-3">
            <Link
              to="/customer-management/customers/$customerId"
              params={{ customerId: String(customerId) }}
              search={{ current: 1, pageSize: 20 }}
            >
              <Button>返回客户档案</Button>
            </Link>
            <Button htmlType="submit" type="primary" loading={save.isPending}>
              保存设置
            </Button>
          </div>
        </div>
        <FormAnchorNav className="hidden lg:block" sections={sections} />
      </div>
    </Form>
  )
}
